import { CsvExportModule } from '@ag-grid-community/csv-export';
import { AgGridReact } from '@ag-grid-community/react';
import { ActionTypes, SecondaryApiTypeMap, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { ApiConnectionUserMapping } from '@tassat/fred-extended-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { getCsvExportParams } from 'Lib/agGridHelper';
import { formatEpochDate } from 'Lib/stringHelpers';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { ActionButtonsCellRenderer } from 'SharedComponents/cellRenderComponent/actionButtonsCellRenderer';

import { getApiConnectionsColumnDefs } from './apiConnectionsAgGridConfig';

import type { IApiConnectionsAgGridProps } from '.';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';

export function ApiConnectionsAgGrid (props: IApiConnectionsAgGridProps) {
    const {
        rowData,
        exportData,
        setExport,
        features
    } = props;

    const [ gridApi, setGridApi ] = React.useState<any>();
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] = React.useReducer(
        ConfirmationWorkflowReducer,
        DEFAULT_INITIAL_STATE
    );

    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const onGridReady = React.useCallback((params) => {
        if (!params.api.isDestroyed()) {
            setGridApi({ api: params.api });
            params.api.sizeColumnsToFit();
        }
    }, []);

    const deleteSecondaryApiConnection = async (apiConnectionUserMap: ApiConnectionUserMapping) => {
        const secondaryConnectionTypeName = apiConnectionUserMap?.apiConnection &&
            SecondaryApiTypeMap[apiConnectionUserMap?.apiConnection.APISecondaryConnectionType].displayValue;

        try {
            const response = await Worker.getInstance()
                .deleteAPIConnectionUser(apiConnectionUserMap.apiUser.APIConnectionUserId);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: 'Successfully Deleted API Connection',
                        successBody: `Successfully deleted the API connection ${secondaryConnectionTypeName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: 'Failed to Delete API Connection',
                        failureBody: `Could not delete the API connection ${secondaryConnectionTypeName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else {
                throw new Error();
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: 'Failed to Delete API Connection',
                    failureBody: `Something went wrong deleting API connection ${secondaryConnectionTypeName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const deleteSecondaryApiConnectionValidation = (apiConnectionUserMap: ApiConnectionUserMapping): boolean => {
        const secondaryConnectionTypeName = apiConnectionUserMap?.apiConnection &&
            SecondaryApiTypeMap[apiConnectionUserMap?.apiConnection.APISecondaryConnectionType].displayValue;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnectionUserMap,
                actionType: ActionTypes.delete,
                confirmTitle: 'Confirming Deleting API Connection',
                confirmBody: `Please confirm deleting the API Connection ${secondaryConnectionTypeName}.
                Note: This will delete your API Key. To connect to Fireblocks again, a new key will need to be generated.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    React.useEffect(() => {
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                preActionValidationAndConfig: { [ActionTypes.delete]: deleteSecondaryApiConnectionValidation },
                onConfirm: { [ActionTypes.delete]: deleteSecondaryApiConnection },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    React.useEffect(() => {
        const gridResize = () => gridApi && gridApi.api.sizeColumnsToFit();

        window.addEventListener('resize', gridResize);

        return () => window.removeEventListener('resize', gridResize);
    }, [gridApi]);

    React.useEffect(() => {
        if (exportData) {
            setExport(false);
            const columnsToExport = features.fireblocks.api ?
                [
                    'apiConnection.APIConnectionName',
                    'apiUser.ApiKey',
                    'apiConnection.ClientId',
                    'apiUser.APIConfirmCode',
                    'apiUser.CreatedAt'
                ]
                : [
                    'apiConnection.APIConnectionName',
                    'apiConnection.ClientId',
                    'apiUser.APIConfirmCode',
                    'apiUser.CreatedAt'
                ];
            //AG Grid export
            gridApi.api.exportDataAsCsv(getCsvExportParams('Lynq_Platform_API_Pair_List', {
                formattingCallback: formatDateForExport,
                columnKeys: columnsToExport
            }));
        }
    }, [exportData]);

    const formatDateForExport = (cell) => {
        if (cell.column.colId === 'apiUser.CreatedAt') {
            return formatEpochDate(cell.value);
        } else {
            return cell.value;
        }
    };

    const columnDefs = getApiConnectionsColumnDefs(features.fireblocks.api);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <div
                className='ag-theme-material'
                id='apiConnectionsGrid'
                style={{ height: '100%' }}
            >
                <AgGridReact
                    animateRows={false}
                    columnDefs={columnDefs}
                    columnMenu='legacy'
                    components={{ actionButtonsCellRenderer: ActionButtonsCellRenderer }}
                    defaultColDef={{
                        resizable: true,
                        filter: true,
                        sortable: true
                    }}
                    enableCellTextSelection={true}
                    modules={[CsvExportModule]}
                    onGridReady={onGridReady}
                    rowData={rowData}
                    suppressCellFocus={true}
                    suppressContextMenu={true}
                />
            </div>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}